import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useRef, useState } from 'react';
// utils && hooks
import { DrugManufacturer, ProductInterface } from 'common/types';
import { useUser } from 'utils/hooks';
import { EDrugManufacturerTypeId } from 'utils/enums/drugmanufacturerTypeId/DrugManufacturerTypeId';
import { useDrugManufacturersContext } from './productComparison/drugManufacturers-context';
import { useProductsContext } from './productComparison/products-context';
import { getProductSelectedByDrugManufacturerId } from 'utils/productComparison/getProductSelectedByDrugManufacturerId';

interface ProductComparisonParams {
    children: ReactNode;
    orderId: any;
}

export interface EstimatedSavingsProps {
    youCanSave: number;
    youAreSaving: number;
}

export type InsufficientCreditWarning = {
    id: number;
    isWarningOpen: boolean;
};

export interface ProductComparisonProps extends ProductComparisonParams {
    retryLoading: boolean;
    setRetryLoading(value: boolean): void;
    selectAllProductByDMId(drug_manufacturer_id: number): void;
    disabledButtonMakeOrderByDelete: boolean;
    recommendations: any;
    setRecommendations: (recommendations: any) => void;
    recommendationsVisible: any;
    setRecommendationsVisible: (recommendations: any) => void;
    setIsFetchRehydrate: (val: boolean) => void;
    isFetchRehydrate: boolean;
    modalPriceWithOutSelection?: any;
    setOpenModalConfigDistribution?: (value: boolean) => void;
    openModalConfigDistribution?: boolean;
    setOpenModalConfigPriorities?: (value: boolean) => void;
    openModalConfigPriorities?: boolean;
    setShowModalPriceWithOutSelection?: (value: boolean) => void;
    showModalPriceWithOutSelection?: boolean;
    getCreditById?: any;
    manuallySelectedProductIds?: number[];
    setManuallySelectedProductIds?: Dispatch<SetStateAction<number[]>>;
    recommendationLimit: boolean;
    setRecommendationLimit: (value: boolean) => void;
    addNewRecommendation: (item: ProductInterface) => void;
    setOpenModal: (v: boolean) => void;
    openModal: boolean;
    setDisabledButtonMakeOrderByDelete: (v: boolean) => void;
}

const ProductComparisonContext = createContext<Omit<ProductComparisonProps, 'children'>>(null);

export function ProductComparisonProvider({ children, ...props }: ProductComparisonParams) {
    const { drugManufacturer } = useDrugManufacturersContext();
    const [retryLoading, setRetryLoading] = useState<boolean>(false);
    const [disabledButtonMakeOrderByDelete, setDisabledButtonMakeOrderByDelete] = useState<boolean>(false);
    const [manuallySelectedProductIds, setManuallySelectedProductIds] = useState<number[]>([]);
    const [recommendationLimit, setRecommendationLimit] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState(false);

    const { user } = useUser({});
    const EX5937 = user?.EX5937; // ff split purchase

    const [openModalConfigDistribution, setOpenModalConfigDistribution] = useState(false);
    const [openModalConfigPriorities, setOpenModalConfigPriorities] = useState(false);
    const [showModalPriceWithOutSelection, setShowModalPriceWithOutSelection] = useState(false);

    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [recommendationsVisible, setRecommendationsVisible] = useState<any>([]);
    const [isFetchRehydrate, setIsFetchRehydrate] = useState(false);
    const modalPriceWithOutSelection = useRef();

    const { products, setProducts } = useProductsContext();

    const addNewRecommendation = (item: ProductInterface) => {
        // const newArray = [item, ...productGroups];
        // setProductGroups(newArray);
        // setProductInfo(item);
    };

    const getCreditById = useCallback(
        (id: DrugManufacturer['id'], selectedItem) => {
            const limitCreditAvailable = drugManufacturer.find((drug) => {
                return drug.id === id;
            })?.credit_available;
            const totalItemSelected = getProductSelectedByDrugManufacturerId(id, products, EX5937).find(
                (item) => selectedItem?.barcode === item?.barcode,
            );
            let subtotal = getProductSelectedByDrugManufacturerId(id, products, EX5937)?.reduce((count, item) => {
                if (selectedItem?.barcode === item?.barcode) {
                    count += selectedItem?.amountSelected;
                } else {
                    count += item?.amountSelected;
                }
                return count;
            }, 0);

            if (totalItemSelected === undefined) {
                subtotal += selectedItem?.amountSelected;
            }

            return limitCreditAvailable >= subtotal || !limitCreditAvailable;
        },
        [products, drugManufacturer],
    );

    const selectAllProductByDMId = useCallback(
        (drug_manufacturer_id: number) => {
            const productsSelected = products
                ?.map((product, index) => {
                    if (product?.quantity !== 0) {
                        const drugLowest = drugManufacturer?.find((d) => +d?.id === product?.lowest[0]);
                        const isVendorActive = !!drugLowest?.vendor_accepted;
                        const drugVendor = drugLowest?.type_id === EDrugManufacturerTypeId.vendor;
                        if (drugVendor && product?.products[drug_manufacturer_id] === null) {
                            // Si la droguería a checkear es vendor + esta activa + tiene stock
                            if (isVendorActive) {
                                const currentProductSelected =
                                    product.products[drug_manufacturer_id] !== null
                                        ? drug_manufacturer_id
                                        : product.lowest[0];
                                return {
                                    ...product,
                                    drugManufacturerIdSelected: [currentProductSelected],
                                };
                            } else {
                                // Si la droguería a checkear es vendor y no esta activa, mantenemos selección
                                return { ...product };
                            }
                        } else {
                            const currentProductSelected =
                                product.products[drug_manufacturer_id] !== null
                                    ? drug_manufacturer_id
                                    : product.lowest[0];
                            return {
                                ...product,
                                drugManufacturerIdSelected: [currentProductSelected],
                            };
                        }
                    }
                })
                .filter((x) => x !== undefined);
            setProducts([...productsSelected]);
        },
        [products],
    );

    return (
        <ProductComparisonContext.Provider
            value={{
                getCreditById,
                retryLoading,
                setRetryLoading,
                setDisabledButtonMakeOrderByDelete,
                selectAllProductByDMId,
                disabledButtonMakeOrderByDelete,
                addNewRecommendation,
                recommendations,
                setRecommendations,
                recommendationsVisible,
                setRecommendationsVisible,
                setIsFetchRehydrate,
                isFetchRehydrate,
                modalPriceWithOutSelection,
                openModalConfigDistribution,
                setOpenModalConfigDistribution,
                openModalConfigPriorities,
                setOpenModalConfigPriorities,
                showModalPriceWithOutSelection,
                setShowModalPriceWithOutSelection,
                manuallySelectedProductIds,
                setManuallySelectedProductIds,
                recommendationLimit,
                setRecommendationLimit,
                setOpenModal,
                openModal,
                ...props,
            }}
        >
            {children}
        </ProductComparisonContext.Provider>
    );
}

export function useProductComparison() {
    const context = useContext(ProductComparisonContext);
    if (!context) {
        throw new Error('useProductComparison must be used within a ProducComparisonProvider');
    }

    return context;
}
